import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, RefreshCw, Plus, ChevronDown, ChevronUp, Search,
  AlertTriangle, Shield, Activity, Users, Store, UserCheck,
  Eye, Flag, CheckCircle, XCircle, Clock, TrendingUp, Filter
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { API_URL } from '../../utils/apiConfig';

const RiskLevelBadge = ({ level }) => {
  const colors = {
    low: 'bg-slate-600 text-slate-200',
    medium: 'bg-amber-600 text-amber-100',
    high: 'bg-orange-600 text-orange-100',
    critical: 'bg-red-600 text-red-100',
  };
  return (
    <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${colors[level] || 'bg-slate-600'}`}>
      {level}
    </span>
  );
};

const AlertStatusBadge = ({ status }) => {
  const colors = {
    new: 'bg-blue-600 text-blue-100',
    under_review: 'bg-purple-600 text-purple-100',
    escalated: 'bg-red-600 text-red-100',
    false_positive: 'bg-slate-500 text-slate-200',
    confirmed: 'bg-orange-600 text-orange-100',
    resolved: 'bg-emerald-600 text-emerald-100',
  };
  return (
    <span className={`px-2 py-1 rounded text-xs font-medium ${colors[status] || 'bg-slate-600'}`}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
};

export default function AdminRiskView() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [trends, setTrends] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [expandedId, setExpandedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [error, setError] = useState('');
  
  // Create alert modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    risk_level: 'medium',
    indicator_type: 'manual_flag',
    sender_id: ''
  });
  const [creating, setCreating] = useState(false);

  const getToken = () => localStorage.getItem('pecash_token');

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/risk/dashboard`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    }
  }, []);

  const fetchAlerts = useCallback(async () => {
    try {
      let url = `${API_URL}/api/admin/risk/alerts?limit=100`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (levelFilter) url += `&risk_level=${levelFilter}`;
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || []);
      }
    } catch (err) {
      console.error('Alerts fetch error:', err);
    }
  }, [statusFilter, levelFilter]);

  const fetchTrends = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/risk/trends?days=30`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTrends(data);
      }
    } catch (err) {
      console.error('Trends fetch error:', err);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchAlerts(), fetchTrends()]);
      setLoading(false);
    };
    load();
  }, [fetchDashboard, fetchAlerts, fetchTrends]);

  useEffect(() => {
    fetchAlerts();
  }, [statusFilter, levelFilter, fetchAlerts]);

  const handleCreateAlert = async () => {
    setCreating(true);
    setError('');
    
    try {
      const res = await fetch(`${API_URL}/api/admin/risk/alerts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(createForm)
      });
      
      if (res.ok) {
        setShowCreateModal(false);
        setCreateForm({
          title: '',
          description: '',
          risk_level: 'medium',
          indicator_type: 'manual_flag',
          sender_id: ''
        });
        await Promise.all([fetchDashboard(), fetchAlerts()]);
        setActiveTab('alerts');
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to create alert');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateAlert = async (alertId, updates) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/risk/alerts/${alertId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      
      if (res.ok) {
        await Promise.all([fetchDashboard(), fetchAlerts()]);
      } else {
        const err = await res.json();
        setError(err.detail || 'Update failed');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const handleEscalate = async (alertId) => {
    const reason = prompt('Enter reason for escalation (min 10 chars):');
    if (!reason || reason.length < 10) {
      setError('Escalation reason must be at least 10 characters');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/admin/risk/alerts/${alertId}/escalate?reason=${encodeURIComponent(reason)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (res.ok) {
        await Promise.all([fetchDashboard(), fetchAlerts()]);
      } else {
        const err = await res.json();
        setError(err.detail || 'Escalation failed');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-red-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/dashboard')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Shield className="w-6 h-6 text-red-400" />
                Risk Dashboard
              </h1>
              <p className="text-sm text-slate-400">Fraud indicators & suspicious activity</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {dashboard?.alert_counts?.total_active > 0 && (
              <span className="px-3 py-1 bg-red-900/50 border border-red-700 rounded-full text-red-300 text-xs font-medium animate-pulse">
                {dashboard.alert_counts.total_active} ACTIVE ALERTS
              </span>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-800 border border-red-700/50 rounded-xl p-4"
            >
              <p className="text-red-400 text-xs mb-1">Critical Alerts</p>
              <p className="text-3xl font-bold text-white">{dashboard.alert_counts?.by_level?.critical || 0}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-slate-800 border border-orange-700/50 rounded-xl p-4"
            >
              <p className="text-orange-400 text-xs mb-1">High Alerts</p>
              <p className="text-3xl font-bold text-white">{dashboard.alert_counts?.by_level?.high || 0}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-slate-800 border border-amber-700/50 rounded-xl p-4"
            >
              <p className="text-amber-400 text-xs mb-1">Medium Alerts</p>
              <p className="text-3xl font-bold text-white">{dashboard.alert_counts?.by_level?.medium || 0}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-slate-800 border border-blue-700/50 rounded-xl p-4"
            >
              <p className="text-blue-400 text-xs mb-1">New Alerts</p>
              <p className="text-3xl font-bold text-white">{dashboard.alert_counts?.by_status?.new || 0}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-slate-800 border border-purple-700/50 rounded-xl p-4"
            >
              <p className="text-purple-400 text-xs mb-1">Failed Limits Today</p>
              <p className="text-3xl font-bold text-white">{dashboard.metrics?.failed_limit_checks_today || 0}</p>
            </motion.div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="bg-red-900/30 border border-red-700 rounded-xl p-4 mb-6">
            <p className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> {error}
            </p>
            <Button size="sm" variant="ghost" className="mt-2 text-red-400" onClick={() => setError('')}>
              Dismiss
            </Button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-700 pb-4 flex-wrap">
          <Button
            data-testid="risk-tab-dashboard"
            variant={activeTab === 'dashboard' ? 'default' : 'ghost'}
            className={activeTab === 'dashboard' ? 'bg-red-600' : 'text-slate-400'}
            onClick={() => setActiveTab('dashboard')}
          >
            <Activity className="w-4 h-4 mr-1" /> Dashboard
          </Button>
          <Button
            data-testid="risk-tab-alerts"
            variant={activeTab === 'alerts' ? 'default' : 'ghost'}
            className={activeTab === 'alerts' ? 'bg-red-600' : 'text-slate-400'}
            onClick={() => setActiveTab('alerts')}
          >
            <AlertTriangle className="w-4 h-4 mr-1" /> Alerts ({alerts.length})
          </Button>
          <Button
            data-testid="risk-tab-anomalies"
            variant={activeTab === 'anomalies' ? 'default' : 'ghost'}
            className={activeTab === 'anomalies' ? 'bg-red-600' : 'text-slate-400'}
            onClick={() => setActiveTab('anomalies')}
          >
            <TrendingUp className="w-4 h-4 mr-1" /> Anomalies
          </Button>
          <Button
            data-testid="risk-create-alert"
            variant="ghost"
            className="text-red-400 ml-auto"
            onClick={() => setShowCreateModal(true)}
          >
            <Flag className="w-4 h-4 mr-1" /> Flag Issue
          </Button>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && dashboard && (
          <div className="space-y-6">
            {/* Detected Anomalies Summary */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                Detected Anomalies (Auto-Detection)
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-900 rounded-lg p-4">
                  <p className="text-slate-400 text-sm">Velocity Anomalies</p>
                  <p className="text-2xl font-bold text-amber-400">{dashboard.detected_anomalies?.velocity_anomalies || 0}</p>
                  <p className="text-slate-500 text-xs mt-1">High transaction frequency</p>
                </div>
                <div className="bg-slate-900 rounded-lg p-4">
                  <p className="text-slate-400 text-sm">Near-Limit Patterns</p>
                  <p className="text-2xl font-bold text-orange-400">{dashboard.detected_anomalies?.near_limit_patterns || 0}</p>
                  <p className="text-slate-500 text-xs mt-1">Repeated ~$200 transactions</p>
                </div>
                <div className="bg-slate-900 rounded-lg p-4">
                  <p className="text-slate-400 text-sm">Multi-Sender Beneficiaries</p>
                  <p className="text-2xl font-bold text-red-400">{dashboard.detected_anomalies?.multi_sender_beneficiary || 0}</p>
                  <p className="text-slate-500 text-xs mt-1">3+ senders to same beneficiary</p>
                </div>
              </div>
            </div>

            {/* Recent Alerts */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4">Recent Alerts</h3>
              {dashboard.recent_alerts?.length > 0 ? (
                <div className="space-y-3">
                  {dashboard.recent_alerts.map((alert) => (
                    <div key={alert.id} className="flex items-center justify-between bg-slate-900 rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <RiskLevelBadge level={alert.risk_level} />
                        <div>
                          <p className="text-white text-sm font-medium">{alert.title}</p>
                          <p className="text-slate-500 text-xs">{alert.indicator_type?.replace(/_/g, ' ')}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <AlertStatusBadge status={alert.status} />
                        <span className="text-slate-500 text-xs">{new Date(alert.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">No recent alerts - system is healthy</p>
              )}
            </div>

            {/* Risk Indicators Info */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4">Active Risk Indicators</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="bg-slate-900 rounded-lg p-3 border border-slate-700">
                  <p className="text-white text-sm font-medium">Unusual Transaction Velocity</p>
                  <p className="text-slate-500 text-xs">5+ transactions in 24 hours</p>
                </div>
                <div className="bg-slate-900 rounded-lg p-3 border border-slate-700">
                  <p className="text-white text-sm font-medium">Repeated Near-Max</p>
                  <p className="text-slate-500 text-xs">Multiple $180+ transactions</p>
                </div>
                <div className="bg-slate-900 rounded-lg p-3 border border-slate-700">
                  <p className="text-white text-sm font-medium">Multi-Sender Pattern</p>
                  <p className="text-slate-500 text-xs">3+ senders to same beneficiary</p>
                </div>
                <div className="bg-slate-900 rounded-lg p-3 border border-slate-700">
                  <p className="text-white text-sm font-medium">Failed Redemptions</p>
                  <p className="text-slate-500 text-xs">Repeated QR/access failures</p>
                </div>
                <div className="bg-slate-900 rounded-lg p-3 border border-slate-700">
                  <p className="text-white text-sm font-medium">Auth Failures</p>
                  <p className="text-slate-500 text-xs">Login/OTP/PIN failures</p>
                </div>
                <div className="bg-slate-900 rounded-lg p-3 border border-slate-700">
                  <p className="text-white text-sm font-medium">Contact Changes</p>
                  <p className="text-slate-500 text-xs">Recent phone/email changes</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Alerts Tab */}
        {activeTab === 'alerts' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex gap-4 mb-4">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="">All Statuses</option>
                <option value="new">New</option>
                <option value="under_review">Under Review</option>
                <option value="escalated">Escalated</option>
                <option value="confirmed">Confirmed</option>
                <option value="false_positive">False Positive</option>
                <option value="resolved">Resolved</option>
              </select>
              <select
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
                className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="">All Levels</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            {/* Alerts List */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-900 border-b border-slate-700">
                  <tr>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Alert</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Level</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Type</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Status</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Created</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {alerts.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center text-slate-500 py-8">
                        No risk alerts found
                      </td>
                    </tr>
                  ) : (
                    alerts.map((alert) => (
                      <React.Fragment key={alert.id}>
                        <tr 
                          className="hover:bg-slate-700/50 cursor-pointer"
                          onClick={() => setExpandedId(expandedId === alert.id ? null : alert.id)}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {expandedId === alert.id ? (
                                <ChevronUp className="w-4 h-4 text-slate-500" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-slate-500" />
                              )}
                              <div>
                                <p className="text-white text-sm font-medium">{alert.title}</p>
                                <p className="text-slate-500 text-xs">{alert.sender_id?.slice(0, 12)}...</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3"><RiskLevelBadge level={alert.risk_level} /></td>
                          <td className="px-4 py-3 text-slate-400 text-sm">{alert.indicator_type?.replace(/_/g, ' ')}</td>
                          <td className="px-4 py-3"><AlertStatusBadge status={alert.status} /></td>
                          <td className="px-4 py-3 text-slate-400 text-xs">{new Date(alert.created_at).toLocaleString()}</td>
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <div className="flex gap-2">
                              {alert.status === 'new' && (
                                <Button
                                  size="sm"
                                  className="text-xs bg-purple-600 hover:bg-purple-700"
                                  onClick={() => handleUpdateAlert(alert.id, { status: 'under_review' })}
                                >
                                  Review
                                </Button>
                              )}
                              {['new', 'under_review'].includes(alert.status) && (
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  className="text-xs"
                                  onClick={() => handleEscalate(alert.id)}
                                >
                                  Escalate
                                </Button>
                              )}
                              {['under_review', 'escalated', 'confirmed'].includes(alert.status) && (
                                <>
                                  <Button
                                    size="sm"
                                    className="text-xs bg-slate-600 hover:bg-slate-500"
                                    onClick={() => handleUpdateAlert(alert.id, { status: 'false_positive' })}
                                  >
                                    False +
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="text-xs bg-emerald-600 hover:bg-emerald-700"
                                    onClick={() => handleUpdateAlert(alert.id, { status: 'resolved' })}
                                  >
                                    Resolve
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                        {expandedId === alert.id && (
                          <tr className="bg-slate-900">
                            <td colSpan="6" className="px-4 py-4">
                              <div className="space-y-3">
                                <div>
                                  <p className="text-slate-500 text-xs">Description</p>
                                  <p className="text-white text-sm">{alert.description}</p>
                                </div>
                                <div className="grid grid-cols-3 gap-4">
                                  <div>
                                    <p className="text-slate-500 text-xs">Alert ID</p>
                                    <p className="text-white font-mono text-xs">{alert.id}</p>
                                  </div>
                                  <div>
                                    <p className="text-slate-500 text-xs">Created By</p>
                                    <p className="text-white text-sm">{alert.created_by}</p>
                                  </div>
                                  <div>
                                    <p className="text-slate-500 text-xs">Assigned To</p>
                                    <p className="text-white text-sm">{alert.assigned_to || 'Unassigned'}</p>
                                  </div>
                                </div>
                                {alert.notes?.length > 0 && (
                                  <div>
                                    <p className="text-slate-500 text-xs mb-2">Notes</p>
                                    {alert.notes.map((note, i) => (
                                      <div key={i} className="bg-slate-800 rounded p-2 mb-1">
                                        <p className="text-white text-sm">{note.text}</p>
                                        <p className="text-slate-500 text-xs">{note.added_by} - {new Date(note.added_at).toLocaleString()}</p>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Anomalies Tab */}
        {activeTab === 'anomalies' && dashboard && (
          <div className="space-y-6">
            {/* Velocity Anomalies */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-amber-400" />
                Velocity Anomalies ({dashboard.anomaly_details?.velocity?.length || 0})
              </h3>
              {dashboard.anomaly_details?.velocity?.length > 0 ? (
                <div className="space-y-2">
                  {dashboard.anomaly_details.velocity.map((a, i) => (
                    <div key={i} className="bg-slate-900 rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm">{a.sender_email}</p>
                        <p className="text-slate-500 text-xs">{a.transaction_count} transactions, ${a.total_usd?.toFixed(2)} total</p>
                      </div>
                      <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={() => {
                        setCreateForm({
                          ...createForm,
                          title: `High velocity: ${a.sender_email}`,
                          description: `${a.transaction_count} transactions totaling $${a.total_usd?.toFixed(2)} detected`,
                          sender_id: a.sender_id,
                          indicator_type: 'unusual_tx_velocity'
                        });
                        setShowCreateModal(true);
                      }}>
                        <Flag className="w-3 h-3 mr-1" /> Flag
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500">No velocity anomalies detected</p>
              )}
            </div>

            {/* Near-Limit Patterns */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-orange-400" />
                Near-Limit Patterns ({dashboard.anomaly_details?.near_limit?.length || 0})
              </h3>
              {dashboard.anomaly_details?.near_limit?.length > 0 ? (
                <div className="space-y-2">
                  {dashboard.anomaly_details.near_limit.map((a, i) => (
                    <div key={i} className="bg-slate-900 rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm">{a.sender_email}</p>
                        <p className="text-slate-500 text-xs">{a.near_limit_count} near-max transactions: ${a.amounts?.join(', $')}</p>
                      </div>
                      <Button size="sm" className="bg-orange-600 hover:bg-orange-700" onClick={() => {
                        setCreateForm({
                          ...createForm,
                          title: `Near-limit pattern: ${a.sender_email}`,
                          description: `${a.near_limit_count} transactions near $200 ceiling detected`,
                          sender_id: a.sender_id,
                          risk_level: 'high',
                          indicator_type: 'repeated_near_max'
                        });
                        setShowCreateModal(true);
                      }}>
                        <Flag className="w-3 h-3 mr-1" /> Flag
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500">No near-limit patterns detected</p>
              )}
            </div>

            {/* Multi-Sender Patterns */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-red-400" />
                Multi-Sender Beneficiaries ({dashboard.anomaly_details?.multi_sender?.length || 0})
              </h3>
              {dashboard.anomaly_details?.multi_sender?.length > 0 ? (
                <div className="space-y-2">
                  {dashboard.anomaly_details.multi_sender.map((a, i) => (
                    <div key={i} className="bg-slate-900 rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm">{a.beneficiary_phone}</p>
                        <p className="text-slate-500 text-xs">{a.unique_senders} different senders, ${a.total_usd?.toFixed(2)} total</p>
                      </div>
                      <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => {
                        setCreateForm({
                          ...createForm,
                          title: `Multi-sender pattern: ${a.beneficiary_phone}`,
                          description: `${a.unique_senders} different senders funding same beneficiary`,
                          risk_level: 'high',
                          indicator_type: 'multi_sender_same_beneficiary'
                        });
                        setShowCreateModal(true);
                      }}>
                        <Flag className="w-3 h-3 mr-1" /> Flag
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500">No multi-sender patterns detected</p>
              )}
            </div>
          </div>
        )}

        {/* Create Alert Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-lg w-full">
              <div className="p-6">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Flag className="w-5 h-5 text-red-400" />
                  Flag Risk Issue
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Title</label>
                    <input
                      type="text"
                      value={createForm.title}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                      placeholder="Brief description of the issue"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Description</label>
                    <textarea
                      value={createForm.description}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white h-24"
                      placeholder="Detailed explanation of the suspicious activity"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">Risk Level</label>
                      <select
                        value={createForm.risk_level}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, risk_level: e.target.value }))}
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">Indicator Type</label>
                      <select
                        value={createForm.indicator_type}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, indicator_type: e.target.value }))}
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                      >
                        <option value="manual_flag">Manual Flag</option>
                        <option value="unusual_tx_velocity">Unusual Velocity</option>
                        <option value="repeated_near_max">Repeated Near-Max</option>
                        <option value="multi_sender_same_beneficiary">Multi-Sender</option>
                        <option value="auth_failures">Auth Failures</option>
                        <option value="account_takeover_indicator">Account Takeover</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Sender ID (optional)</label>
                    <input
                      type="text"
                      value={createForm.sender_id}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, sender_id: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                      placeholder="UUID of the sender"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-slate-700">
                  <Button variant="ghost" onClick={() => setShowCreateModal(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateAlert}
                    disabled={creating || !createForm.title || !createForm.description || createForm.description.length < 10}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {creating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Flag className="w-4 h-4 mr-2" />}
                    Create Alert
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
